import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Flex, HStack, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Slider, SliderFilledTrack, SliderThumb,
  SliderTrack, Spinner, Stack, Text, VStack, useToast,
} from '@chakra-ui/react';
import {
  decodeFile, encodeMp3, extractSlice, previewSlice, samplePeaks,
} from './audioClip';

const CLIP_SECONDS = 60;
const WAVE_BARS = 400;

/**
 * Modal that lets the user scrub a 60-second window over a full-length
 * audio file and export just that slice as a WAV. Rendered by
 * LibraryPage when the picked track is longer than the clip target,
 * otherwise skipped entirely.
 *
 * Flow:
 *   1. `file` prop changes → decode with Web Audio → sample waveform
 *   2. User drags the start slider (or taps a bar) to move the window
 *   3. Preview plays a live AudioBufferSource of just those 60s
 *   4. On confirm, extract slice → WAV blob → wrap in a File → onConfirm()
 *
 * Fails gracefully: unsupported codec / decode error surfaces as a
 * readable message and the "Use anyway" escape opens the current path
 * (send the whole file, byte-clipped at 8MB in fileToReferenceAudio).
 */
export interface AudioClipPickerModalProps {
  file: File | null;                    // null = closed
  onCancel: () => void;
  onConfirm: (clippedFile: File, startSec: number) => void;
  onSkip: () => void;                    // "use anyway" — send original
}

export function AudioClipPickerModal({ file, onCancel, onConfirm, onSkip }: AudioClipPickerModalProps) {
  const toast = useToast();
  const [buffer, setBuffer] = useState<AudioBuffer | null>(null);
  const [peaks, setPeaks] = useState<Float32Array | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [decoding, setDecoding] = useState(false);
  const [startSec, setStartSec] = useState(0);
  const [encoding, setEncoding] = useState(false);
  const previewNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const previewStartCtxTimeRef = useRef<number>(0);
  const previewStartOffsetRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);
  const debounceRef = useRef<number | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [playhead, setPlayhead] = useState<number | null>(null);

  // Decode + waveform-sample the file when it lands / changes.
  useEffect(() => {
    if (!file) {
      setBuffer(null);
      setPeaks(null);
      setLoadError(null);
      setStartSec(0);
      return;
    }
    let cancelled = false;
    setDecoding(true);
    setLoadError(null);
    (async () => {
      try {
        const buf = await decodeFile(file);
        if (cancelled) return;
        setBuffer(buf);
        setPeaks(samplePeaks(buf, WAVE_BARS));
        // Default to the middle of the song — usually the meatiest section.
        // If the song is shorter than the clip window, start at 0.
        const midStart = Math.max(0, buf.duration / 2 - CLIP_SECONDS / 2);
        setStartSec(Math.min(midStart, Math.max(0, buf.duration - CLIP_SECONDS)));
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.message || 'Could not read this file');
      } finally {
        if (!cancelled) setDecoding(false);
      }
    })();
    return () => { cancelled = true; };
  }, [file]);

  // Cleanup any preview node on unmount / new file.
  useEffect(() => () => stopPreview(), []);
  const stopPreview = useCallback(() => {
    if (previewNodeRef.current) {
      try { previewNodeRef.current.stop(); } catch { /* already stopped */ }
      previewNodeRef.current = null;
    }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    setIsPreviewing(false);
    setPlayhead(null);
  }, []);

  const duration = buffer?.duration || 0;
  const maxStart = Math.max(0, duration - CLIP_SECONDS);
  const clipEnd = Math.min(duration, startSec + CLIP_SECONDS);
  const startPct = duration > 0 ? (startSec / duration) * 100 : 0;
  const widthPct = duration > 0 ? Math.min(100 - startPct, (CLIP_SECONDS / duration) * 100) : 0;
  const playheadPct = playhead != null && duration > 0 ? (playhead / duration) * 100 : null;

  const doPreview = useCallback((fromSec: number) => {
    if (!buffer) return;
    // Stop the previous source WITHOUT resetting isPreviewing — a clean
    // "restart from new position" during drag shouldn't blink the toggle.
    if (previewNodeRef.current) {
      try { previewNodeRef.current.stop(); } catch { /* already stopped */ }
      previewNodeRef.current = null;
    }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }

    const clipDur = Math.min(CLIP_SECONDS, duration - fromSec);
    const node = previewSlice(buffer, fromSec, clipDur);
    node.onended = () => {
      setIsPreviewing(false);
      setPlayhead(null);
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
    previewNodeRef.current = node;
    setIsPreviewing(true);

    // Track the playhead through the waveform. AudioContext.currentTime is
    // the authoritative clock — much smoother than a JS-side timer. See
    // audioClip.ts for why we share a single AudioContext.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ctxNow = (previewNodeRef.current?.context as any)?.currentTime ?? 0;
    previewStartCtxTimeRef.current = ctxNow;
    previewStartOffsetRef.current = fromSec;
    const tick = () => {
      const n = previewNodeRef.current;
      if (!n) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const elapsed = (n.context as any).currentTime - previewStartCtxTimeRef.current;
      const pos = previewStartOffsetRef.current + elapsed;
      setPlayhead(pos > duration ? duration : pos);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [buffer, duration]);

  // Debounced auto-play on slider change so scrubbing feels responsive
  // but we don't restart the audio graph on every intermediate value.
  // 220ms is short enough that a "tap and release" is heard immediately,
  // long enough that dragging across the waveform doesn't stutter.
  const scheduleAutoPreview = useCallback((v: number) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      doPreview(v);
    }, 220);
  }, [doPreview]);

  // Auto-preview on first decode so the user immediately hears the
  // default midpoint selection without hunting for the play button.
  useEffect(() => {
    if (buffer && !isPreviewing) {
      const initial = Math.max(0, buffer.duration / 2 - CLIP_SECONDS / 2);
      const start = Math.min(initial, Math.max(0, buffer.duration - CLIP_SECONDS));
      doPreview(start);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffer]);

  const doConfirm = async () => {
    if (!buffer || !file) return;
    stopPreview();
    setEncoding(true);
    // Yield a frame so the "Preparing…" busy state actually paints before
    // the ~500ms encoder loop starts hogging the main thread.
    await new Promise((r) => setTimeout(r, 0));
    try {
      const slice = extractSlice(buffer, startSec, Math.min(CLIP_SECONDS, duration - startSec));
      // MP3 — Mureka rejects WAV ("File format not supported"). See audioClip.ts.
      const blob = encodeMp3(slice);
      const stem = file.name.replace(/\.[^.]+$/, '') || 'clip';
      const clipped = new File([blob], `${stem}-clip.mp3`, { type: 'audio/mpeg' });
      onConfirm(clipped, startSec);
    } catch (err: any) {
      // The previous version had a bare try/finally that swallowed encoder
      // failures silently — the button "did nothing" because encodeMp3
      // threw and no toast surfaced. Never repeat that.
      // eslint-disable-next-line no-console
      console.error('[MixGenie] clip encode failed:', err);
      toast({
        title: 'Could not prepare the clip',
        description: err?.message || 'Encoding failed — try Skip to send the raw file.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setEncoding(false);
    }
  };

  const handleCancel = () => { stopPreview(); onCancel(); };
  const handleSkip = () => { stopPreview(); onSkip(); };

  // If the song is already ≤ clip length, no need to show the picker —
  // this component shouldn't have been opened. Guard anyway.
  const alreadyShortEnough = buffer && duration <= CLIP_SECONDS + 0.5;
  useEffect(() => {
    if (alreadyShortEnough && file) {
      // Just hand back the original file as-is.
      onConfirm(file, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyShortEnough]);

  return (
    <Modal
      isOpen={!!file}
      onClose={handleCancel}
      isCentered
      size={{ base: 'full', md: 'lg' }}
      scrollBehavior="inside"
      closeOnEsc={!encoding}
      closeOnOverlayClick={!encoding}
    >
      <ModalOverlay />
      <ModalContent bg="night.800" color="whiteAlpha.900">
        <ModalHeader>
          <Stack spacing={0}>
            <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="0.08em">
              Reference audio
            </Text>
            <Text fontSize="md" noOfLines={1}>
              {encoding ? 'Preparing…' : 'Pick a 60-second slice'}
            </Text>
          </Stack>
        </ModalHeader>
        {!encoding && <ModalCloseButton />}
        <ModalBody>
          {/* WAV encode + parent hand-off is a few seconds on mobile —
              lock the whole modal to a busy state so the user can't tap
              the slider / cancel / anything during that window. */}
          {encoding && (
            <VStack spacing={5} py={{ base: 14, md: 20 }}>
              <Spinner size="xl" color="pink.300" thickness="3px" />
              <Stack spacing={1} textAlign="center">
                <Text color="whiteAlpha.900" fontWeight={700} fontSize="lg">
                  Preparing your clip…
                </Text>
                <Text color="whiteAlpha.600" fontSize="sm">
                  Encoding audio and opening the generator
                </Text>
              </Stack>
            </VStack>
          )}
          {!encoding && decoding && (
            <Flex align="center" justify="center" py={12}>
              <HStack spacing={3}>
                <Spinner size="md" color="pink.300" />
                <Text color="whiteAlpha.800">Loading waveform…</Text>
              </HStack>
            </Flex>
          )}
          {!encoding && loadError && (
            <Stack spacing={3} py={4}>
              <Text color="red.300" fontSize="sm">{loadError}</Text>
              <Text color="whiteAlpha.700" fontSize="sm">
                MixGenie can still use the raw file if you skip the picker — it'll head-clip the first 8 MB.
              </Text>
            </Stack>
          )}
          {!encoding && buffer && peaks && !loadError && (
            <Stack spacing={4}>
              <Waveform peaks={peaks} startPct={startPct} widthPct={widthPct} playheadPct={playheadPct} />
              <Stack spacing={1}>
                <Slider
                  value={startSec}
                  min={0}
                  max={maxStart}
                  step={0.5}
                  onChange={(v) => {
                    // Update immediately so the highlighted region and the
                    // start/end labels track the drag with zero lag.
                    setStartSec(v);
                    // Restart preview from the new position, debounced so
                    // fast drags don't strobe the audio graph.
                    scheduleAutoPreview(v);
                  }}
                  aria-label="Clip start"
                >
                  <SliderTrack bg="whiteAlpha.200"><SliderFilledTrack bg="pink.400" /></SliderTrack>
                  <SliderThumb boxSize={4} />
                </Slider>
                <Flex justify="space-between" fontSize="xs" color="whiteAlpha.700">
                  <Text>Start {formatTime(startSec)}</Text>
                  <Text>End {formatTime(clipEnd)}</Text>
                  <Text>Track {formatTime(duration)}</Text>
                </Flex>
              </Stack>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          {!encoding && (
          <HStack spacing={2} w="full" justify="space-between">
            <Button variant="ghost" colorScheme="whiteAlpha" onClick={handleSkip} size="sm">
              Skip — use full file
            </Button>
            <HStack spacing={2}>
              {buffer && (
                <Button
                  variant="outline"
                  colorScheme="whiteAlpha"
                  onClick={isPreviewing ? stopPreview : () => doPreview(startSec)}
                  size="sm"
                >
                  {isPreviewing ? '❚❚ Pause' : '▶ Play'}
                </Button>
              )}
              <Button
                colorScheme="pink"
                onClick={doConfirm}
                isDisabled={!buffer || decoding}
                isLoading={encoding}
              >
                Use this clip
              </Button>
            </HStack>
          </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Waveform strip with the selected 60s highlighted. Simple absolute-
 * positioned overlay — sits at the top of the range slider so the
 * relationship between the two is obvious.
 */
function Waveform({ peaks, startPct, widthPct, playheadPct }: {
  peaks: Float32Array;
  startPct: number;
  widthPct: number;
  playheadPct: number | null;
}) {
  // Precompute bar heights (memoized via useMemo — peaks reference is
  // stable within a session for the same file).
  const bars = useMemo(() => Array.from(peaks), [peaks]);
  return (
    <Box position="relative" h="90px" w="full" bg="rgba(10, 8, 20, 0.55)" borderRadius="8px" overflow="hidden">
      {/* selection overlay */}
      <Box
        position="absolute"
        top={0}
        bottom={0}
        left={`${startPct}%`}
        w={`${widthPct}%`}
        bg="pink.500"
        opacity={0.22}
        pointerEvents="none"
      />
      {/* playhead line — only shown while previewing so a paused state
          returns to just the selection highlight. */}
      {playheadPct != null && (
        <Box
          position="absolute"
          top={0}
          bottom={0}
          left={`${playheadPct}%`}
          w="2px"
          bg="pink.200"
          boxShadow="0 0 6px rgba(233, 74, 143, 0.9)"
          pointerEvents="none"
        />
      )}
      {/* bars */}
      <Flex position="absolute" inset={0} align="center" px={1} gap="1px">
        {bars.map((p, i) => (
          <Box
            key={i}
            flex="1"
            h={`${Math.max(2, p * 100)}%`}
            bg="whiteAlpha.500"
            borderRadius="1px"
            minW="1px"
          />
        ))}
      </Flex>
    </Box>
  );
}

function formatTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
}
